import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";

import ModalComponent from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import TextInput from "../../../components/TextInput";
import MuiDatePicker from "../../../components/MuiDatePicker";

const REQUIRED_DATE_TYPES = ["PASSPORT", "VISA", "DRIVING_LICENCE", "SSN"];

const candidateDocumentSchema = Yup.object().shape({
  documentType: Yup.string()
    .required("Document type is required")
    .test("not-empty", "Document type is required", (value) => {
      return value !== null && value !== undefined && value.trim() !== "";
    }),
  file: Yup.mixed()
    .required("File is required")
    .test("file-exists", "File is required", (value) => {
      return value !== null && value !== undefined;
    }),
  others: Yup.string().when("documentType", {
    is: "OTHER",
    then: () => Yup.string()
      .required("Please specify document type")
      .test("not-empty", "Please specify document type", (value) => {
        return value !== null && value !== undefined && value.trim() !== "";
      }),
    otherwise: () => Yup.string().notRequired(),
  }),
  issueDate: Yup.string().when("documentType", {
    is: (value) => REQUIRED_DATE_TYPES.includes((value || "").toUpperCase()),
    then: () => Yup.string().required("Issue date is required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  expiryDate: Yup.string().when("documentType", {
    is: (value) => REQUIRED_DATE_TYPES.includes((value || "").toUpperCase()),
    then: () => Yup.string().required("Expiry date is required"),
    otherwise: () => Yup.string().notRequired(),
  }),
});

const CandidateDocumentModal = ({
  show,
  onClose,
  title,
  initialValues,
  documentsList,
  submitForm,
}) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setOptions(
      (documentsList || []).map((item) => ({
        label: item.label,
        value: item.value,
      }))
    );
  }, [documentsList]);

  const formattedInitialValues = useMemo(
    () => ({
      ...initialValues,
      file: null,
    }),
    [initialValues]
  );

  const shouldShowDates = (documentType) => {
    const normalized = (documentType || "").toUpperCase();
    if (normalized === "RESUME" || normalized === "OTHER") {
      return false;
    }
    return true;
  };

  const modalTitle = title || "Upload Document";

  return (
    <ModalComponent show={show} onClose={onClose} title={modalTitle}>
      <Formik
        initialValues={formattedInitialValues}
        enableReinitialize
        validationSchema={candidateDocumentSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values, { resetForm, setSubmitting }) => {
          // Validate before submission
          if (!values.documentType || values.documentType.trim() === "") {
            setSubmitting(false);
            return;
          }
          if (!values.file) {
            setSubmitting(false);
            return;
          }

          const baseType =
            values.documentType === "OTHER"
              ? values.others?.trim().toUpperCase()
              : values.documentType;

          const payload = {
            ...values,
            documentType: baseType ? baseType.toUpperCase() : "",
          };

          submitForm(payload);
          
          onClose();
          resetForm();
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setFieldTouched,
        }) => {
          const displayDates = shouldShowDates(values.documentType);

          return (
            <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 py-3">
              <div className="space-y-2">
                <CustomDropdown
                  value={
                    options.find((item) => item.value === values.documentType) ||
                    null
                  }
                  name="documentType"
                  handleChange={(selected) => {
                    const nextValue = selected?.value || null;
                    setFieldValue("documentType", nextValue, true);
                    setFieldTouched("documentType", true, false);

                    if (nextValue !== "OTHER") {
                      setFieldValue("others", "", false);
                    }

                    if (!shouldShowDates(nextValue)) {
                      setFieldValue("issueDate", "", false);
                      setFieldValue("expiryDate", "", false);
                    }
                  }}
                  customClasses="w-full text-left"
                  placeholder="Select Type of Document"
                  options={options}
                  handleGetOptionValue={(option) => option.value}
                  handleGetOptionName={(option) => option.label}
                />
                {errors.documentType && touched.documentType && (
                  <div className="text-red-500 text-sm">
                    {errors.documentType}
                  </div>
                )}

                {values.documentType === "OTHER" && (
                  <div className="space-y-2 pt-3">
                    <TextInput
                      type="text"
                      name="others"
                      handleChange={handleChange}
                      value={values.others}
                      placeholder="Enter Document Type"
                      classes="w-full"
                    />
                    {errors.others && touched.others && (
                      <div className="text-red-500 text-sm">
                        {errors.others}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <input
                  type="file"
                  id="file"
                  name="file"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0] || null;
                    setFieldValue("file", file, true);
                    setFieldTouched("file", true, false);
                  }}
                  className="block w-full px-3 py-1 border border-gray-300 h-[2.5rem] rounded-md focus:ring-1 focus:ring-primary"
                />
                {errors.file && touched.file && (
                  <div className="text-red-500 text-sm">{errors.file}</div>
                )}
              </div>

              {displayDates && (
                <div className="space-y-2 relative overflow-visible">
                  <MuiDatePicker
                    label="Issue Date"
                    value={
                      values.issueDate ? moment(values.issueDate, "MM-DD-YYYY") : null
                    }
                    onChange={(date) => {
                      if (date) {
                        setFieldValue("issueDate", date.format("MM-DD-YYYY"));
                      } else {
                        setFieldValue("issueDate", "");
                      }
                    }}
                  />
                  {errors.issueDate && touched.issueDate && (
                    <div className="text-red-500 text-sm">
                      {errors.issueDate}
                    </div>
                  )}
                </div>
              )}

              {displayDates && (
                <div className="space-y-2 relative overflow-visible">
                  <MuiDatePicker
                    label="Expiry Date"
                    value={
                      values.expiryDate
                        ? moment(values.expiryDate, "MM-DD-YYYY")
                        : null
                    }
                    onChange={(date) => {
                      if (date) {
                        setFieldValue("expiryDate", date.format("MM-DD-YYYY"));
                      } else {
                        setFieldValue("expiryDate", "");
                      }
                    }}
                    minDate={
                      values.issueDate
                        ? moment(values.issueDate, "MM-DD-YYYY")
                        : null
                    }
                  />
                  {errors.expiryDate && touched.expiryDate && (
                    <div className="text-red-500 text-sm">
                      {errors.expiryDate}
                    </div>
                  )}
                </div>
              )}

              <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="bg-gray-300 text-black h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-gray-400"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary text-white h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-primaryDark"
                >
                  Submit
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ModalComponent>
  );
};

export default CandidateDocumentModal;

